/**
 * /app/{tenantSlug}/settings/custom-fields — Settings → custom employee fields.
 *
 * Batch 32 Track A. HR-admin (or anyone holding customfield.admin) defines
 * tenant-specific employee fields with a type system, validation, section
 * grouping and per-field view/edit capability gates (drawn from the RBAC
 * catalog). Fields render dynamically on the employee profile via
 * CustomFieldsRenderer.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useCapabilityCatalog,
  useCreateCustomField,
  useCustomFields,
  useDeleteCustomField,
  useEffectiveCapabilities,
} from "@/hooks/use-custom-rbac";
import type {
  CustomFieldDefinition,
  CustomFieldType,
} from "@/lib/api/custom-fields";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/custom-fields",
)({
  component: SettingsCustomFieldsRoute,
});

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Select (one)" },
  { value: "multiselect", label: "Select (many)" },
  { value: "boolean", label: "Yes / No" },
];

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------

function CreateFieldDialog({ capabilityTokens }: { capabilityTokens: string[] }) {
  const [open, setOpen] = useState(false);
  const [fieldKey, setFieldKey] = useState("");
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType>("text");
  const [section, setSection] = useState("General");
  const [required, setRequired] = useState(false);
  const [optionsRaw, setOptionsRaw] = useState("");
  const [viewCap, setViewCap] = useState("employee.view");
  const [editCap, setEditCap] = useState("employee.edit");
  const create = useCreateCustomField();

  const isChoice = fieldType === "select" || fieldType === "multiselect";

  const reset = () => {
    setFieldKey("");
    setLabel("");
    setFieldType("text");
    setSection("General");
    setRequired(false);
    setOptionsRaw("");
    setViewCap("employee.view");
    setEditCap("employee.edit");
  };

  const onSubmit = async () => {
    const options = optionsRaw
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await create.mutateAsync({
        field_key: fieldKey.trim().toLowerCase(),
        label: label.trim(),
        field_type: fieldType,
        required,
        section: section.trim() || "General",
        display_order: 0,
        validation: isChoice ? { options } : {},
        view_capability: viewCap,
        edit_capability: editCap,
      });
      toast.success("Custom field created.");
      setOpen(false);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <PlusCircle className="h-4 w-4" />
          New field
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create custom field</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="cf-label">Label *</Label>
              <Input
                id="cf-label"
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                  if (fieldKey === "")
                    setFieldKey(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "_")
                        .replace(/^_+|_+$/g, ""),
                    );
                }}
                placeholder="Shift Pattern"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-key">Key *</Label>
              <Input
                id="cf-key"
                value={fieldKey}
                onChange={(e) =>
                  setFieldKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))
                }
                placeholder="shift_pattern"
                className="font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Type *</Label>
              <Select value={fieldType} onValueChange={(v) => setFieldType(v as CustomFieldType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf-section">Section</Label>
              <Input
                id="cf-section"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                placeholder="Statutory"
              />
            </div>
          </div>

          {isChoice && (
            <div className="space-y-1.5">
              <Label htmlFor="cf-options">Options (one per line) *</Label>
              <textarea
                id="cf-options"
                value={optionsRaw}
                onChange={(e) => setOptionsRaw(e.target.value)}
                rows={4}
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                placeholder={"Morning\nEvening\nNight"}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>View permission</Label>
              <CapabilitySelect value={viewCap} onChange={setViewCap} tokens={capabilityTokens} />
            </div>
            <div className="space-y-1.5">
              <Label>Edit permission</Label>
              <CapabilitySelect value={editCap} onChange={setEditCap} tokens={capabilityTokens} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={required} onCheckedChange={(c) => setRequired(c === true)} />
            Required on create
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              disabled={create.isPending || label.trim() === "" || fieldKey.trim() === ""}
            >
              {create.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Create field
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CapabilitySelect({
  value,
  onChange,
  tokens,
}: {
  value: string;
  onChange: (v: string) => void;
  tokens: string[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {tokens.map((t) => (
          <SelectItem key={t} value={t} className="font-mono text-xs">
            {t}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function SettingsCustomFieldsRoute() {
  const fieldsQ = useCustomFields();
  const catalogQ = useCapabilityCatalog();
  const { bypass, has } = useEffectiveCapabilities();
  const del = useDeleteCustomField();

  const canAdmin = bypass || has("customfield.admin");
  const capabilityTokens = useMemo(
    () => (catalogQ.data?.items ?? []).map((c) => c.token),
    [catalogQ.data],
  );
  const fields = fieldsQ.data?.items ?? [];
  const active = fields.filter((f) => f.is_active);

  const onDelete = async (f: CustomFieldDefinition) => {
    try {
      await del.mutateAsync(f.id);
      toast.success("Field deleted.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Custom employee fields</h2>
              <p className="text-sm text-muted-foreground">
                Add your own fields to the employee record. Each field can be
                gated by capability so sensitive data stays restricted.
              </p>
            </div>
            {canAdmin && <CreateFieldDialog capabilityTokens={capabilityTokens} />}
          </div>

          {!canAdmin && (
            <Alert>
              <AlertTitle>Restricted</AlertTitle>
              <AlertDescription>
                You need the custom-field administration capability to manage
                fields.
              </AlertDescription>
            </Alert>
          )}

          {fieldsQ.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {fieldsQ.data && active.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No custom fields yet.
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {active.map((f) => (
              <Card key={f.id}>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <CardTitle className="text-base">{f.label}</CardTitle>
                        <Badge variant="outline" className="text-[10px]">
                          {f.field_type}
                        </Badge>
                        {f.required && (
                          <Badge variant="secondary" className="text-[10px]">
                            required
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 font-mono text-xs text-muted-foreground">
                        {f.field_key} · {f.section} · view: {f.view_capability} ·
                        edit: {f.edit_capability}
                      </p>
                    </div>
                    {canAdmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Delete field &quot;{f.label}&quot;?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Stored values are kept but hidden. This removes
                              the field from the employee profile.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDelete(f)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </SettingsShell>
    </>
  );
}
